import { flatMap } from 'lodash/fp';
import { createSelector } from 'reselect';
import { ApplicationState, CommanderThunkAction, TaxValue, Taxation } from '@mews-commander/types';
import {
    DateService,
    DateTimeService,
    DateTimeUtcService,
    GainsightService,
    lt,
    NumberService,
    showErrorAlert,
    showSuccessAlert,
    TimeService,
} from '@mews/framework';
import {
    GeneralEnterprise,
    GeneralPricingMode,
    getRepositoryQueryKey,
    ProductServiceOrderAddParameters,
} from '@mews-commander/data-access';
import { compactArray, getStringProp } from '@mews/fp';
import { push } from '@mews/routing';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import {
    getCurrentCurrencyCultureInfo,
    getCurrentNumberCultureInfo,
    getCurrentDateTimeLocalizationInfo,
} from '@mews-commander/core/legacy-globalization';
import { addProductServiceOrders } from '@mews-commander/legacy-entities/src/actions/productServiceOrders';
import { getTaxationsByTaxEnvironmentCode } from '@mews-commander/legacy-entities/src/ducks/taxEnvironments';
import {
    getCurrentEnterpriseTaxEnvironmentCode,
    getCurrentEnterprise,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
} from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { doesServiceHaveOverridablePrice } from '@mews-commander/legacy-entities/src/ducks/services';
import * as fieldNames from '../constants';
import { ProductUnits, ServiceFormValues } from '../types';
import { getDefaultProductServiceAmount } from '../selectors';
import { calculateTaxValue } from '../../billing/selectors';
import { popHistory } from '../../../core/history';

interface AddProductServiceOrderArgs {
    billId: string | undefined;
    values: Omit<ServiceFormValues, typeof fieldNames.CUSTOMER_ID | typeof fieldNames.SERVICE_ID> & {
        serviceId: string;
        customerId: string;
    };
}

const PRODUCT_SERVICE_ORDER_SUBMIT_NEGATIVE_AMOUNT_EVENT_NAME = 'ProductServiceOrderSubmitNegativeAmount';

export const trackNegativeAmountSubmitted = (
    gainsightService: GainsightService,
    data: ProductServiceOrderAddParameters
) => {
    const { productOrders, unitAmount } = data;

    const unitAmountIsNegativeValue = unitAmount.value < 0;
    const productOrdersHaveNegativeValue = productOrders.filter(
        ({ count, unitCost }) => (unitCost && unitCost < 0) || (count && count < 0)
    ).length;

    if (unitAmountIsNegativeValue || productOrdersHaveNegativeValue) {
        gainsightService.trackEvent(PRODUCT_SERVICE_ORDER_SUBMIT_NEGATIVE_AMOUNT_EVENT_NAME, data);
    }
};

export const getDefaultProductTaxRates = (
    state: ApplicationState,
    taxRateFieldValue: Record<string, string | null>,
    unitCost: number
): TaxValue[] => {
    const code = getCurrentEnterpriseTaxEnvironmentCode(state);
    const taxations: Taxation[] = getTaxationsByTaxEnvironmentCode(state, { taxEnvironmentCode: code });
    const taxRates = flatMap((taxation) => taxation.taxRates, taxations);

    return compactArray(
        Object.values(taxRateFieldValue).map((taxRateCode) => {
            if (!taxRateCode) return null;

            return calculateTaxValue(taxRateCode, taxRates, unitCost);
        })
    );
};

const getDefaultProductPrice = (state: ApplicationState, serviceId: string, amountValue?: string) => {
    const cultureInfo = getCurrentNumberCultureInfo(state);

    if (!amountValue) {
        const amount = getDefaultProductServiceAmount(state, { serviceId });

        return NumberService.unformat(amount, cultureInfo);
    }

    return NumberService.unformat(amountValue, cultureInfo);
};

const getDateTimeUtcString = createSelector(
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    getCurrentDateTimeLocalizationInfo,
    getStringProp('dateString'),
    getStringProp('shortTimeString'),
    (timezone: string, localization, dateString, shortTimeString) => {
        const parsedDate = DateService.fromString(dateString, localization, timezone);
        const parsedTime = TimeService.fromShortString(shortTimeString, localization);
        const dateTime = DateTimeService.fromDateAndTime(parsedDate, parsedTime);

        const dateTimeUtc = DateTimeService.toUtc(dateTime);
        return DateTimeUtcService.toISOString(dateTimeUtc);
    }
);

const getDefaultProductFromValues = (
    state: ApplicationState,
    { billId, values }: AddProductServiceOrderArgs
): Omit<ProductServiceOrderAddParameters, 'productOrders'> => {
    const enteprise: GeneralEnterprise = getCurrentEnterprise(state);
    const hasEnterpriseNetPricing = enteprise.pricing === GeneralPricingMode.Net;
    const priceValue = getDefaultProductPrice(state, values[fieldNames.SERVICE_ID], values[fieldNames.AMOUNT]);

    const taxRateFieldValue = values[fieldNames.TAX_RATE] || {};

    const taxValues = getDefaultProductTaxRates(state, taxRateFieldValue, priceValue);

    const startUtc = getDateTimeUtcString(state, {
        dateString: values[fieldNames.DATE] as string,
        shortTimeString: values[fieldNames.TIME] as string,
    });

    return {
        serviceId: values[fieldNames.SERVICE_ID],
        startUtc,
        billId,
        accountId: values[fieldNames.CUSTOMER_ID],
        note: values[fieldNames.NOTES],
        segmentId: values[fieldNames.SEGMENT],
        unitAmount: {
            taxValues,
            currency: values[fieldNames.CURRENCY],
            ...(hasEnterpriseNetPricing ? { netValue: priceValue } : { grossValue: priceValue }),
            value: priceValue,
        },
    };
};

const getProductOrdersFromValues = (
    state: ApplicationState,
    { serviceId, units }: { serviceId: string; units: Record<string, ProductUnits> }
) => {
    const numberCultureInfo = getCurrentNumberCultureInfo(state);
    const currencyCultureInfo = getCurrentCurrencyCultureInfo(state);
    const hasOverridablePrice = doesServiceHaveOverridablePrice(state, { id: serviceId });

    return Object.keys(units).map((productId) => {
        const { unitCost, unitAmount } = units[productId];
        const unformattedUnitCost = unitCost ? NumberService.unformat(unitCost, currencyCultureInfo) : 0;
        const unformattedUnitAmount = unitAmount ? NumberService.unformat(unitAmount, numberCultureInfo) : 0;

        return {
            productId,
            count: unformattedUnitAmount,
            unitCost: hasOverridablePrice ? unformattedUnitCost : null,
        };
    });
};

export const addProductServiceOrder =
    ({ billId, values }: AddProductServiceOrderArgs): CommanderThunkAction =>
    async (dispatch, getState, { gainsightService, reactQueryClient }) => {
        const state = getState();

        const enterpriseId = getCurrentEnterpriseId();

        const defaultProductOrder = getDefaultProductFromValues(state, { billId, values });
        const productOrders = values[fieldNames.UNITS]
            ? getProductOrdersFromValues(state, {
                  serviceId: values[fieldNames.SERVICE_ID],
                  units: values[fieldNames.UNITS],
              })
            : [];

        const mappedProductServiceOrderData = {
            ...defaultProductOrder,
            productOrders,
        };

        return dispatch(
            addProductServiceOrders({ enterpriseId, productServiceOrders: [mappedProductServiceOrderData] })
        )
            .then(async () => {
                const url = popHistory();
                dispatch(push(url));

                trackNegativeAmountSubmitted(gainsightService, mappedProductServiceOrderData);
                showSuccessAlert({ description: lt('SaveSuccessMessage') });
                await reactQueryClient.invalidateQueries({ queryKey: getRepositoryQueryKey('productChecklist') });
            })
            .catch(({ message }) => {
                return showErrorAlert({ description: <>{message}</> });
            });
    };
